// ────────────────────────────────────────────────────────────────────
// AssetNavSidebar — the shared chrome around AssetNavPanel (header + collapse
// toggle + collapsed strip). Used identically by the Site Map and the asset
// workspace so the worklist reads as one persistent tool across the workflow.
// ────────────────────────────────────────────────────────────────────

import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Asset } from "@/lib/types";
import { AssetNavPanel } from "./AssetNavPanel";
import { useSidebarCollapsed } from "./WorklistContext";

interface Props {
  /** Source pool to list. */
  assets: Asset[];
  /** Plant scope for the shared filter/expand state (see WorklistContext). */
  plantId?: number;
  /** Highlight the asset currently open (asset workspace). */
  activeAssetId?: number;
  /** Source query still pending. */
  loading?: boolean;
  /** Message when the source pool is empty. */
  emptyHint?: string;
}

export function AssetNavSidebar({
  assets, plantId, activeAssetId, loading, emptyHint,
}: Props): JSX.Element {
  // Collapse state is shared (see WorklistContext) so it follows across screens.
  const [collapsed, setCollapsed] = useSidebarCollapsed();

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        title="Show assets"
        className="w-8 shrink-0 flex items-start justify-center pt-3 border-r hover:bg-[rgba(var(--accent-rgb)_/_0.05)] transition-colors"
        style={{ borderColor: "var(--hairline)", color: "var(--ink-3)" }}
      >
        <ChevronRight size={16} />
      </button>
    );
  }

  return (
    <aside className="w-[360px] shrink-0 flex flex-col min-h-0 border-r" style={{ borderColor: "var(--hairline)" }}>
      <div className="px-3 h-10 border-b flex items-center justify-between shrink-0" style={{ borderColor: "var(--hairline)" }}>
        <span className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: ".2em", color: "var(--ink-3)" }}>Assets</span>
        <button onClick={() => setCollapsed(true)} title="Collapse" className="hover:text-[var(--accent)] transition-colors" style={{ color: "var(--ink-3)" }}>
          <ChevronLeft size={14} />
        </button>
      </div>
      <AssetNavPanel
        assets={assets}
        plantId={plantId}
        activeAssetId={activeAssetId}
        loading={loading}
        emptyHint={emptyHint}
        className="flex-1"
      />
    </aside>
  );
}
